import { ComponentType } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CartesiaVoice from '@/components/organisms/Integrations/VoiceCommon/cards/CartesiaVoice';
import ElevenLabsVoice from '@/components/organisms/Integrations/VoiceCommon/cards/ElevenLabsVoice';
import MiniMaxVoice from '@/components/organisms/Integrations/VoiceCommon/cards/MiniMaxVoice';
import { STTDynamicSettings } from '@/components/organisms/Integrations/VoiceCommon/cards/STTDynamicSettings';
import { STTModelSelector } from '@/components/organisms/Integrations/VoiceCommon/cards/STTModelSelector';
import { TTSModelSelector } from '@/components/organisms/Integrations/VoiceCommon/cards/TTSModelSelector';
import { TTSCardProps } from '@/components/organisms/Integrations/VoiceCommon/cards/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PickerCombobox, PickerGroup, PickerOption } from '@/components/ui/custom/PickerCombobox';
import { FormDescription, FormItem, FormLabel } from '@/components/ui/form';
import {
  PLATFORM_DEFAULT,
  SpeechSource,
  useSpeechStep,
} from '@/modules/aiAgents/hooks/useSpeechStep';
import { DEVELOPERS_SETTINGS_PATHS } from '@/modules/developerTools/constants';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';
import {
  SPEECH_PROVIDER_ICONS,
  SPEECH_PROVIDER_NAMES,
} from '@/modules/developerTools/pages/speechModels/helper';
import { SpeechModelProvider } from '@/modules/developerTools/types';
import { selectAccountSlug } from '@/redux/session/selectors';
import { SpeechKind } from '@/shared/types/speechStrategy';
import { TTSProvider } from '@/shared/types/voiceProviders';

import { CATALOG_TTS_PROVIDERS, CatalogVoiceCard } from './CatalogVoiceCard';
import { RegionDefaultSummary } from './RegionDefaultSummary';
import { providerIcon } from '../ModelStrategy/ModelPicker';

const TTS_CARDS: Record<TTSProvider, ComponentType<TTSCardProps>> = {
  elevenlabs: ElevenLabsVoice,
  cartesia: CartesiaVoice,
  minimax: MiniMaxVoice,
};

/** The speech models page's logo, else the neutral glyph. */
const speechProviderIcon = (provider: SpeechModelProvider) =>
  SPEECH_PROVIDER_ICONS[provider] ?? providerIcon(provider);

interface SpeechStepEditorProps {
  kind: SpeechKind;
  /** Form path of the row's step, e.g. `tts.1.step`. */
  name: string;
  language: string;
  /** The agent's primary language row: with no region default, it has nothing to fall back to. */
  isPrimary?: boolean;
  disabled?: boolean;
}

/**
 * One language row of a speech strategy: pick a speech model of the account or a Moveo-hosted
 * provider, then the provider's own settings, the same cards a voice integration shows.
 */
export const SpeechStepEditor = ({
  kind,
  name,
  language,
  isPrimary,
  disabled,
}: SpeechStepEditorProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { defaults } = useSpeechModels();
  const regionDefault = defaults?.[kind]?.[language]?.[0];
  const {
    step,
    configPath,
    sources,
    sourceValue,
    selectSource,
    hasModelField,
    modelId,
    selectModel,
    ttsModels,
    sttModels,
    sttModelInfo,
    catalogStatus,
  } = useSpeechStep({ kind, name, language });

  const defaultEntry = () => {
    if (regionDefault) {
      return {
        label: t('ai_agents.speech_strategy.platform_default'),
        note: t('ai_agents.speech_strategy.region_default_help', {
          0: SPEECH_PROVIDER_NAMES[regionDefault.provider],
        }),
      };
    }
    if (isPrimary) {
      return {
        label: t('ai_agents.speech_strategy.no_default'),
        note:
          kind === 'stt'
            ? t('ai_agents.speech_strategy.no_default_stt_help')
            : t('ai_agents.speech_strategy.no_default_tts_help'),
      };
    }
    return {
      label: t('ai_agents.speech_strategy.same_as_primary'),
      note: t('ai_agents.speech_strategy.same_as_primary_help'),
    };
  };
  const { label: defaultLabel, note: defaultNote } = defaultEntry();
  const selected = sources.find((source) => source.value === sourceValue);
  const toOption = (source: SpeechSource): PickerOption => ({
    value: source.value,
    label: source.label,
    icon: speechProviderIcon(source.provider),
    keywords: [source.label],
  });
  const groups: PickerGroup[] = [
    { key: 'default', options: [{ value: PLATFORM_DEFAULT, label: defaultLabel }] },
    {
      key: 'own',
      heading: t('ai_agents.speech_strategy.own_group'),
      options: sources.filter((source) => !source.hosted).map(toOption),
    },
    {
      key: 'hosted',
      heading: t('ai_agents.speech_strategy.hosted_group'),
      options: sources.filter((source) => source.hosted).map(toOption),
    },
  ];

  const ttsProvider = step && kind === 'tts' ? step.provider : undefined;
  const TtsCard = ttsProvider ? TTS_CARDS[ttsProvider as TTSProvider] : undefined;
  const catalogProvider = CATALOG_TTS_PROVIDERS.find((provider) => provider === ttsProvider);

  const sourceNote = () => {
    if (!step) {
      return defaultNote;
    }
    const providerName = SPEECH_PROVIDER_NAMES[step.provider];
    if (!step.speech_model_id) {
      return t('ai_agents.speech_strategy.hosted_help', { 0: providerName });
    }
    const keyName = selected?.keyName ?? providerName;
    if (step.provider === 'elevenlabs' && kind === 'tts') {
      return t('ai_agents.speech_strategy.own_key_voices_help', { 0: keyName });
    }
    if (TtsCard) {
      return t('ai_agents.speech_strategy.own_key_shared_voices_help', {
        0: keyName,
        1: providerName,
      });
    }
    return t('ai_agents.speech_strategy.own_key_help', { 0: keyName });
  };

  return (
    <div className="flex flex-col gap-6">
      <FormItem>
        <FormLabel>{t('ai_agents.speech_strategy.source')}</FormLabel>
        <PickerCombobox
          groups={groups}
          value={sourceValue}
          onSelect={selectSource}
          footer={{
            to: `/${slug}${DEVELOPERS_SETTINGS_PATHS.speechModels}`,
            label: t('ai_agents.speech_strategy.add_new'),
          }}
          disabled={disabled}
          aria-label={t('ai_agents.speech_strategy.source')}
        >
          <span className="flex items-center gap-2 truncate">
            {selected && speechProviderIcon(selected.provider)}
            <span className="truncate">
              {sourceValue === PLATFORM_DEFAULT ? defaultLabel : selected?.label}
            </span>
          </span>
        </PickerCombobox>
        <FormDescription>{sourceNote()}</FormDescription>
      </FormItem>

      {!step && regionDefault && <RegionDefaultSummary kind={kind} step={regionDefault} />}

      {step?.speech_model_id && catalogStatus === 'error' && (
        <Alert variant="destructive">
          <AlertTriangleIcon />
          <AlertDescription>
            {t('ai_agents.speech_strategy.key_load_failed', {
              0: selected?.keyName ?? SPEECH_PROVIDER_NAMES[step.provider],
            })}
          </AlertDescription>
        </Alert>
      )}

      {TtsCard && hasModelField && (
        <TTSModelSelector
          value={modelId ?? ''}
          models={ttsModels}
          disabled={disabled}
          onChange={selectModel}
        />
      )}
      {TtsCard && step && (
        <fieldset disabled={disabled} className="contents">
          <TtsCard basePath={configPath} strategy={{ speechModelId: step.speech_model_id }} />
        </fieldset>
      )}
      {catalogProvider && (
        <fieldset disabled={disabled} className="contents">
          <CatalogVoiceCard
            key={catalogProvider}
            provider={catalogProvider}
            basePath={configPath}
          />
        </fieldset>
      )}

      {step && kind === 'stt' && hasModelField && (
        <STTModelSelector
          value={modelId ?? ''}
          models={sttModels}
          disabled={disabled}
          onChange={selectModel}
        />
      )}
      {sttModelInfo && (
        <fieldset disabled={disabled} className="contents">
          <STTDynamicSettings modelInfo={sttModelInfo} basePath={configPath} />
        </fieldset>
      )}
    </div>
  );
};
